import {Box, Checkbox, IconButton, Spinner} from '@primer/react'
import {useCallback, useRef, useState} from 'react'
import {IssueOpenedIcon} from '@primer/octicons-react'
import {handleItemToggle, type TaskItem} from '../use-tasklist'
import type {ListItem} from '@github-ui/markdown-editor/list-editing'
import {DragAndDrop, useDragAndDrop} from '@github-ui/drag-and-drop'
import styles from './TaskListItem.module.css'
import {SafeHTMLBox} from '@github-ui/safe-html'
import {TaskListMenu} from './TaskListMenu'
import {LABELS} from '../constants/labels'

export type TaskListItemProps = {
  markdownValue: string
  onChange: (markdown: string) => void | Promise<void>
  onConvertToIssue?: (task: TaskItem, setIsConverting: (converting: boolean) => void) => void
  markdownIndex: number
  nested?: boolean
  position?: number
  item: TaskItem
  totalItems: number
  disabled?: boolean
}

type TaskListItem = ListItem & {taskBox: '[ ]' | '[x]'}

// Task list items that are already issues are wrapped in a `<span class="reference">
const IS_ISSUE_REGEX = /span class="reference"/

export function TaskListItem({
  markdownValue,
  onChange,
  onConvertToIssue,
  markdownIndex,
  nested = false,
  position = 1,
  item,
  totalItems,
  disabled,
}: TaskListItemProps) {
  const markdownRef = useRef(markdownValue)
  const [checked, setChecked] = useState(item.checked)
  const [showTrigger, setShowTrigger] = useState(false)
  const [isConverting, setIsConverting] = useState(false)

  const onToggleItem = useCallback(() => {
    handleItemToggle({markdownRef, markdownIndex, checked, onChange, setChecked})
  }, [checked, onChange, markdownIndex])

  const {moveToPosition} = useDragAndDrop()
  const {index} = item

  const handleMove = ({moveAction, e}: {moveAction: string; e: React.MouseEvent<HTMLLIElement, MouseEvent>}) => {
    e.preventDefault()
    e.stopPropagation()
    switch (moveAction) {
      case 'up':
        moveToPosition(index, index - 1, true)
        break
      case 'down':
        moveToPosition(index, index + 1, false)
        break
      default:
        break
    }
  }

  const isIssue = IS_ISSUE_REGEX.test(item.content)
  const tasklistIstemTestIdBase = `tasklist-item-${position}-${markdownIndex}`

  return (
    <Box
      className={`${nested && !onConvertToIssue ? styles['no-convert-task-list-item'] : styles['task-list-item']}`}
      onMouseEnter={() => setShowTrigger(true)}
      onMouseLeave={() => setShowTrigger(false)}
      sx={{marginLeft: nested ? `${28 * position}px` : '0px'}}
      data-testid={tasklistIstemTestIdBase}
    >
      <Box className={styles['left-aligned-content']}>
        <Box className={styles['drag-drop-container']}>
          {!nested && (
            <DragAndDrop.DragTrigger
              style={{
                visibility: showTrigger ? 'visible' : 'hidden',
                display: disabled ? 'none' : 'flex',
                width: '6px',
                height: '16px',
                alignItems: 'center',
                marginTop: !nested ? '2px' : 0,
              }}
            />
          )}
        </Box>

        <Box className={styles['checkbox-items']}>
          <Checkbox
            checked={checked}
            onChange={e => {
              e.preventDefault()
              e.stopPropagation()
              onToggleItem()
            }}
            aria-label={`${item.title} checklist item`}
            sx={{height: '12px', width: '12px', marginTop: '4px', ':before': {width: '11px'}}}
          />
          <SafeHTMLBox unverifiedHTML={item.content} className={styles['safe-html']} />
        </Box>
      </Box>
      {isConverting && <Spinner size="small" />}
      {!isConverting && nested && !isIssue && onConvertToIssue && (
        <IconButton
          data-testid={`${tasklistIstemTestIdBase}-convert-button`}
          icon={IssueOpenedIcon}
          aria-labelledby={LABELS.convertToIssue}
          sx={{
            visibility: showTrigger ? 'visible' : 'hidden',
            height: 'fit-content',
          }}
          onClick={() => onConvertToIssue(item, setIsConverting)}
          variant="invisible"
          size={'small'}
        />
      )}
      {!isConverting && !nested && (
        <TaskListMenu
          data-testid={`${tasklistIstemTestIdBase}-menu`}
          onConvertToIssue={() => onConvertToIssue?.(item, setIsConverting)}
          totalItems={totalItems}
          item={item}
          handleMove={handleMove}
          disabled={disabled}
          isIssue={isIssue}
        />
      )}
    </Box>
  )
}

try{ TaskListItem.displayName ||= 'TaskListItem' } catch {}